/**
 * Chat API Service
 *
 * REST endpoint wrappers for the chat module.
 */

import axios from 'axios';
import type {
  Channel,
  ChatMessage,
  ChatMember,
  ChatAttachment,
  ReactionGroup,
  UnreadCount,
  ChatSearchResult,
  ChatBookmark,
  ChatMention,
  CreateChannelRequest,
  SendMessageRequest,
  UploadUrlResponse,
} from '../types/chat';

const apiClient = axios.create({
  baseURL: '/api/chat',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to all requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('ngui_auth_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============================================================
// Channels
// ============================================================

export const chatChannelsApi = {
  list: async (includeArchived = false): Promise<Channel[]> => {
    const response = await apiClient.get('/channels', {
      params: { include_archived: includeArchived },
    });
    return response.data;
  },

  listArchived: async (): Promise<Channel[]> => {
    const response = await apiClient.get('/channels/archived');
    return response.data;
  },

  get: async (channelId: string): Promise<Channel> => {
    const response = await apiClient.get(`/channels/${channelId}`);
    return response.data;
  },

  create: async (data: CreateChannelRequest): Promise<Channel> => {
    const response = await apiClient.post('/channels', data);
    return response.data;
  },

  update: async (channelId: string, data: Partial<CreateChannelRequest>): Promise<Channel> => {
    const response = await apiClient.put(`/channels/${channelId}`, data);
    return response.data;
  },

  delete: async (channelId: string): Promise<void> => {
    await apiClient.delete(`/channels/${channelId}`);
  },

  reorder: async (channelId: string, newPosition: number, newParentId?: string): Promise<void> => {
    await apiClient.post(`/channels/${channelId}/reorder`, {
      new_position: newPosition,
      new_parent_id: newParentId ?? null,
    });
  },

  archive: async (channelId: string): Promise<void> => {
    await apiClient.post(`/channels/${channelId}/archive`);
  },

  unarchive: async (channelId: string): Promise<void> => {
    await apiClient.post(`/channels/${channelId}/unarchive`);
  },

  createDM: async (userId: number): Promise<Channel> => {
    const response = await apiClient.post('/channels/dm', { user_id: userId });
    return response.data;
  },
};

// ============================================================
// Members
// ============================================================

export const chatMembersApi = {
  list: async (channelId: string): Promise<ChatMember[]> => {
    const response = await apiClient.get(`/channels/${channelId}/members`);
    return response.data;
  },

  add: async (channelId: string, userId: number, role = 'member'): Promise<ChatMember> => {
    const response = await apiClient.post(`/channels/${channelId}/members`, {
      user_id: userId,
      role,
    });
    return response.data;
  },

  remove: async (channelId: string, userId: number): Promise<void> => {
    await apiClient.delete(`/channels/${channelId}/members/${userId}`);
  },

  updateRole: async (channelId: string, userId: number, role: string): Promise<void> => {
    await apiClient.put(`/channels/${channelId}/members/${userId}/role`, { role });
  },

  mute: async (channelId: string, muted: boolean): Promise<void> => {
    await apiClient.put(`/channels/${channelId}/mute`, { muted });
  },
};

// ============================================================
// Messages
// ============================================================

export const chatMessagesApi = {
  list: async (channelId: string, beforeId?: string, limit = 50): Promise<ChatMessage[]> => {
    const response = await apiClient.get(`/channels/${channelId}/messages`, {
      params: { before_id: beforeId, limit },
    });
    return response.data;
  },

  send: async (channelId: string, data: SendMessageRequest): Promise<ChatMessage> => {
    const response = await apiClient.post(`/channels/${channelId}/messages`, data);
    return response.data;
  },

  get: async (messageId: string): Promise<ChatMessage> => {
    const response = await apiClient.get(`/messages/${messageId}`);
    return response.data;
  },

  update: async (messageId: string, content: string): Promise<ChatMessage> => {
    const response = await apiClient.put(`/messages/${messageId}`, { content });
    return response.data;
  },

  delete: async (messageId: string): Promise<void> => {
    await apiClient.delete(`/messages/${messageId}`);
  },

  listReplies: async (messageId: string, afterId?: string, limit = 50): Promise<ChatMessage[]> => {
    const response = await apiClient.get(`/messages/${messageId}/replies`, {
      params: { after_id: afterId, limit },
    });
    return response.data;
  },

  markRead: async (channelId: string, lastReadMessageId: string): Promise<void> => {
    await apiClient.post(`/channels/${channelId}/read`, {
      last_read_message_id: lastReadMessageId,
    });
  },

  getUnreadCounts: async (): Promise<UnreadCount[]> => {
    const response = await apiClient.get('/unread');
    return response.data;
  },
};

// ============================================================
// Reactions
// ============================================================

export const chatReactionsApi = {
  toggle: async (messageId: string, emoji: string): Promise<{ action: string }> => {
    const response = await apiClient.post(`/messages/${messageId}/reactions`, { emoji });
    return response.data;
  },

  list: async (messageId: string): Promise<ReactionGroup[]> => {
    const response = await apiClient.get(`/messages/${messageId}/reactions`);
    return response.data;
  },
};

// ============================================================
// Pins
// ============================================================

export const chatPinsApi = {
  list: async (channelId: string): Promise<ChatMessage[]> => {
    const response = await apiClient.get(`/channels/${channelId}/pins`);
    return response.data;
  },

  toggle: async (messageId: string): Promise<{ pinned: boolean }> => {
    const response = await apiClient.post(`/messages/${messageId}/pin`);
    return response.data;
  },
};

// ============================================================
// Attachments / Upload
// ============================================================

export const chatAttachmentsApi = {
  getUploadUrl: async (
    channelId: string,
    fileName: string,
    contentType: string,
    fileSize: number
  ): Promise<UploadUrlResponse> => {
    const response = await apiClient.post(`/channels/${channelId}/upload-to-disk`, {
      file_name: fileName,
      content_type: contentType,
      file_size: fileSize,
    });
    return response.data;
  },

  upload: async (
    uploadUrl: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<void> => {
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': file.type },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    });
  },

  getDownloadUrl: async (
    attachmentId: string
  ): Promise<{ url: string; file_name: string }> => {
    const response = await apiClient.get(`/attachments/${attachmentId}/download`);
    return response.data;
  },

  list: async (
    channelId: string,
    contentType?: string,
    limit = 50,
    offset = 0
  ): Promise<ChatAttachment[]> => {
    const response = await apiClient.get(`/channels/${channelId}/attachments`, {
      params: { content_type: contentType, limit, offset },
    });
    return response.data;
  },
};

// ============================================================
// Search
// ============================================================

export const chatSearchApi = {
  fulltext: async (
    query: string,
    channelId?: string,
    includeArchived = false,
    limit = 50
  ): Promise<ChatSearchResult[]> => {
    const response = await apiClient.get('/search', {
      params: { q: query, channel_id: channelId, include_archived: includeArchived, limit },
    });
    return response.data;
  },

  hashtag: async (
    tag: string,
    includeArchived = false,
    limit = 50,
    offset = 0
  ): Promise<ChatSearchResult[]> => {
    const response = await apiClient.get(`/search/hashtag/${tag}`, {
      params: { include_archived: includeArchived, limit, offset },
    });
    return response.data;
  },
};

// ============================================================
// Bookmarks
// ============================================================

export const chatBookmarksApi = {
  list: async (limit = 50, offset = 0): Promise<ChatBookmark[]> => {
    const response = await apiClient.get('/bookmarks', { params: { limit, offset } });
    return response.data;
  },

  add: async (messageId: string, note?: string): Promise<void> => {
    await apiClient.post(`/messages/${messageId}/bookmark`, { note: note ?? null });
  },

  remove: async (messageId: string): Promise<void> => {
    await apiClient.delete(`/messages/${messageId}/bookmark`);
  },
};

// ============================================================
// Mentions
// ============================================================

export const chatMentionsApi = {
  list: async (limit = 50, offset = 0): Promise<ChatMention[]> => {
    const response = await apiClient.get('/mentions', { params: { limit, offset } });
    return response.data;
  },
};
